import React, { useEffect, useState } from 'react';
import useLocales from 'hooks/useLocales';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { IMe } from 'core/interface/me';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { CMSService } from 'services/cms/cms';
import { RootState } from 'redux/store';
import { CompanyService } from 'services/companes/companies';
import ReactPlayer from 'react-player';
import Image from 'components/image/Image';
import AboutComp from './about';
import JobOpening from './jobOpening';
import Organization from './organization';
import PeopleSay from './peopleSay';
import Culture from './culture';
import Popup from 'components/shared/Popups/Popup/Index';
import CompanyVideo from 'components/companyVideo';
import Benefit from './benefit';
import BeInTouch from 'components/beInTouch';
import Button from 'components/Button/Index';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import Footer from 'components/Footer/Index';
import { initialValues, validation } from './form';
import { ISocialMediaData } from 'core/interface/socialMedia';
import { callForAllElement } from 'helpers/callAll';
import { CircularProgress } from '@mui/material';
import { isAuthorized } from 'utils/isAuthorized';

import './Index.scss';
import Navbar from 'components/Navbar';
import PopUpWrapper from 'components/PopUpWrapper';

// Helper function to clean YouTube URLs
const cleanYouTubeUrl = (url: string): string => {
  try {
    // Check if it's a YouTube URL
    if (url && (url.includes('youtube.com') || url.includes('youtu.be'))) {
      // Extract video ID
      let videoId = '';

      if (url.includes('youtube.com/watch')) {
        // Regular YouTube URL
        const urlObj = new URL(url);
        videoId = urlObj.searchParams.get('v') || '';
      } else if (url.includes('youtu.be/')) {
        // Short YouTube URL
        const parts = url.split('youtu.be/');
        if (parts.length > 1) {
          videoId = parts[1].split('?')[0].split('&')[0];
        }
      }

      if (videoId) {
        // Return clean YouTube URL with just the video ID
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
    }

    // Return original URL if not YouTube or couldn't extract ID
    return url || '';
  } catch (error) {
    console.error('Error cleaning YouTube URL:', error);
    return url || '';
  }
};

interface CareerInfoProps {
  showFooter?: boolean;
}

const CareerInfo = ({ showFooter = true }: CareerInfoProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [openModalContact, setOpenModalContact] = useState(false);
  const { slug } = useParams();
  const me = useSelector((state: IMe) => state.user).user;
  const { cms } = useSelector((state: RootState) => state.cms);

  const isAuthenticated = isAuthorized();
  const isCompanyOwner = !!(
    isAuthenticated &&
    me?.company &&
    me?.company === cms?.company
  );

  const isCareerPage = useLocation().pathname.includes('/companies/');

  const handleSubmit = async (values: any, actions: any) => {
    if (!slug) return;
    try {
      const valuesWithCompanyID = { ...values, company: slug };
      const res = await CMSService.sendContacts(valuesWithCompanyID);
      if (res) {
        enqueueSnackbar(t('Email have been sent'), {
          variant: 'success'
        });
        actions.resetForm();
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      enqueueSnackbar(t('Failed to send email'), {
        variant: 'error'
      });
    }
  };

  const handleChange = (companyData: any) => {
    const formData = new FormData();
    for (const key in companyData) {
      if (key !== 'id') {
        formData.append(key, (companyData as any)[key]);
      }
    }
    CMSService.updateCMS(me?.company, formData);
    handleModal(setOpenModal, openModal);
  };

  const handleModal = (setModal: (value: boolean) => void, toggle: boolean) => {
    setModal(!toggle);
  };

  const handleChangeLinks = (companyData: any) => {
    const { contact_email, contact_phone } = companyData;
    const formData = new FormData();
    contact_email && formData.append('contact_email', contact_email);
    contact_phone && formData.append('contact_phone', contact_phone);
    CMSService.updateCMS(me?.company, formData);
    handleModal(setOpenModalContact, openModalContact);
  };

  const handleSocialLinkChange = (data: any, isNew: boolean) => {
    if (isNew) {
      callForAllElement(data, (link) => {
        return CMSService.updateCMSSocialMediaLinks(me?.company, link);
      });
    } else {
      callForAllElement(data, (link) => {
        return CMSService.updateCMSSocialMediaLink(
          link.page,
          link.changedFields,
          link.id
        );
      });
    }
    handleModal(setOpenModalContact, openModalContact);
  };

  useEffect(() => {
    if (slug) {
      CMSService.getCMS(slug);
      isCompanyOwner && CompanyService.getCompanies(me?.company);
    }
  }, [slug, me, isCompanyOwner]);

  const handleBackClick = (): void => {
    navigate(-1);
  };

  // Get clean video URL if available
  const cleanVideoUrl = cms?.video_url ? cleanYouTubeUrl(cms.video_url) : '';

  return (
    <>
      {isCareerPage && <Navbar />}
      <div className="careerContainer">
        <div
          onClick={handleBackClick}
          className="career_back_button skillio-back-button"
        >
          <Image name="back.svg" width={16} height={16} />
          <span>{t('Back')}</span>
        </div>
        {!cms ? (
          <div className="loadingContainer">
            <CircularProgress className="loading" />
          </div>
        ) : (
          <div className="aboutContainer">
            <AboutComp canEdit={isCompanyOwner} />
            <JobOpening canEdit={isCompanyOwner} />
            <Organization canEdit={isCompanyOwner} />
            <Culture canEdit={isCompanyOwner} />
            <PeopleSay canEdit={isCompanyOwner} />
            <Benefit canEdit={isCompanyOwner} />
            <div className="videoContainer">
              <div className="videoWrapper" style={{ marginTop: '96px' }}>
                {isCompanyOwner && (
                  <div
                    className="edit-info"
                    onClick={() => handleModal(setOpenModal, openModal)}
                  >
                    <Image
                      className="pencil"
                      name="pencil.svg"
                      width={13}
                      height={13}
                    />
                  </div>
                )}
                {cleanVideoUrl ? (
                  <ReactPlayer
                    className="react-player"
                    playing={false}
                    light={true}
                    controls={false}
                    url={cleanVideoUrl}
                    width="100%"
                    height="642px"
                  />
                ) : (
                  isCompanyOwner && (
                    <div className="video-placeholder">
                      <h3>{t('Company Video')}</h3>
                      <p>{t('Click the edit button to add a company video')}</p>
                    </div>
                  )
                )}

                {openModal && (
                  <Popup
                    title=""
                    className="expertise"
                    onClose={() => handleModal(setOpenModal, openModal)}
                  >
                    <CompanyVideo
                      companyData={cms}
                      setNewData={handleChange}
                      isOneBtn={true}
                      onClose={() => handleModal(setOpenModal, openModal)}
                      showTopSide={false}
                      isPopup={true}
                    />
                  </Popup>
                )}
              </div>
            </div>
            {isAuthenticated && me?.id && (
              <div className="contactContainer">
                <div className="bgImageWrapper">
                  {isCompanyOwner && (
                    <div
                      className="edit-info"
                      onClick={() =>
                        handleModal(setOpenModalContact, openModalContact)
                      }
                    >
                      <Image
                        className="edit"
                        name="edit.svg"
                        width={13}
                        height={13}
                      />
                    </div>
                  )}
                  <Image className="bgPhoto" name="bg.png" />
                </div>
                {openModalContact && (
                  <PopUpWrapper
                    onClick={() =>
                      handleModal(setOpenModalContact, openModalContact)
                    }
                  >
                    <BeInTouch
                      companyData={cms}
                      setNewData={handleChangeLinks}
                      setLinkData={handleSocialLinkChange}
                      isOneBtn={true}
                      onClose={() =>
                        handleModal(setOpenModalContact, openModalContact)
                      }
                      showTopSide={false}
                      isPopup={true}
                      showTitle={true}
                    />
                  </PopUpWrapper>
                )}
                {isAuthenticated && me?.id && (
                  <div className="formContainer">
                    <div className="contactForm">
                      <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        onSubmit={(values, actions) =>
                          handleSubmit(values, actions)
                        }
                        validationSchema={validation(t)}
                      >
                        {({ setFieldValue, errors }) => {
                          return (
                            <Form>
                              <h1>
                                {t("Fill out the form and we'll be in touch!")}
                              </h1>
                              <FormField
                                inputClassName="input"
                                fieldName="name"
                                placeholder="Name"
                                type={InputType.text}
                              />

                              <FormField
                                inputClassName="input"
                                fieldName="email"
                                placeholder="Email"
                                type={InputType.text}
                              />

                              <div className="field">
                                <textarea
                                  className={`input-formik area ${
                                    errors.message ? 'error' : ''
                                  }`}
                                  placeholder="Message"
                                  onChange={(e) =>
                                    setFieldValue('message', e.target.value)
                                  }
                                />
                              </div>
                              <Button
                                t={t}
                                title="Send"
                                className="send"
                                type="submit"
                              />
                            </Form>
                          );
                        }}
                      </Formik>
                    </div>
                    <div className="infoSection">
                      <div className="pencil_icon">
                        <div className="pencil_circle" />
                        <Image
                          className="email"
                          name="email.svg"
                          width={13}
                          height={13}
                        />
                      </div>
                      <p className="email_text">{cms?.contact_email}</p>
                      <div className="pencil_icon">
                        <div className="pencil_circle" />
                        <Image
                          className="phone"
                          name="phone.svg"
                          width={13}
                          height={13}
                        />
                      </div>
                      <p>{cms?.contact_phone}</p>
                      <div className="socialMediaContainer">
                        {cms?.social_media_links &&
                          cms?.social_media_links.map(
                            (item: ISocialMediaData) => (
                              <a href={item.url} key={item.id} target="blank">
                                <Image
                                  key={item.id}
                                  className="dashboard__left__social-icons"
                                  width={31}
                                  height={31}
                                  name={`${item.network}.svg`}
                                />
                              </a>
                            )
                          )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {showFooter && <Footer />}
    </>
  );
};

export default CareerInfo;
